import React, { useEffect, useRef, useState } from 'react'
import Style from './notification.module.css'
import { useGetUnreadNotificationsQuery } from '../../../../features/notifications/notificationsApi'

const POLL_MS = 60000

const DATE_TIME = { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' }

const formatDateTime = (value) => new Date(value).toLocaleString(undefined, DATE_TIME)

const relativeTime = (value) => {
    const seconds = Math.round((new Date(value).getTime() - Date.now()) / 1000)
    const units = [['year', 31536000], ['month', 2592000], ['week', 604800], ['day', 86400], ['hour', 3600], ['minute', 60]]
    const [unit, size] = units.find(([, length]) => Math.abs(seconds) >= length) || ['second', 1]
    if (unit === 'second') return 'just now'
    return new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' }).format(Math.round(seconds / size), unit)
}

const Notification = () => {
    const [isOpen, setIsOpen] = useState(false)
    const containerRef = useRef(null)
    const { data: notifications = [], isLoading, isError, refetch } = useGetUnreadNotificationsQuery(undefined, {
        pollingInterval: POLL_MS,
        refetchOnFocus: true
    })
    const count = notifications.length

    useEffect(() => {
        if (!isOpen) return undefined
        const handleClick = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) setIsOpen(false)
        }
        const handleKey = (event) => {
            if (event.key === 'Escape') setIsOpen(false)
        }
        document.addEventListener('mousedown', handleClick)
        document.addEventListener('keydown', handleKey)
        return () => {
            document.removeEventListener('mousedown', handleClick)
            document.removeEventListener('keydown', handleKey)
        }
    }, [isOpen])

    return (
        <div className={`dropdown d-inline-block ${Style.container}`} ref={containerRef}>
            <button
                type="button"
                className="btn header-item noti-icon waves-effect"
                id="page-header-notifications-dropdown"
                aria-label={count > 0 ? `Notifications, ${count} unread` : 'Notifications'}
                aria-expanded={isOpen}
                aria-haspopup="true"
                onClick={() => setIsOpen(!isOpen)}
            >
                <i className="uil-bell"></i>
            </button>
            {count > 0 ? <span className={Style.badge} aria-hidden="true">{count > 99 ? '99+' : count}</span> : null}
            {isOpen ? (
                <div className={Style.panel} role="dialog" aria-label="Notifications">
                    <div className={Style.header}>
                        <span className={Style.title}>Notifications</span>
                        {count > 0 ? <span className={Style.count}>{count} unread</span> : null}
                    </div>
                    <div className={Style.body}>
                        {isLoading ? (
                            <p className={Style.state}>Loading notifications...</p>
                        ) : isError ? (
                            <p className={Style.state}>
                                Notifications could not be loaded.{' '}
                                <button type="button" className={Style.retry} onClick={refetch}>Retry</button>
                            </p>
                        ) : count === 0 ? (
                            <div className={Style.state}>
                                <i className={`uil-bell-slash ${Style.stateIcon}`} aria-hidden="true"></i>
                                You're all caught up
                            </div>
                        ) : (
                            <ul className={Style.list}>
                                {notifications.map((item) => (
                                    <li key={item.id} className={Style.item}>
                                        <span className={Style.dot} aria-hidden="true"></span>
                                        <div className={Style.content}>
                                            {item.title ? <p className={Style.itemTitle}>{item.title}</p> : null}
                                            {item.message ? <p className={Style.message}>{item.message}</p> : null}
                                            {item.details.length > 0 ? (
                                                <dl className={Style.details}>
                                                    {item.details.map((detail) => (
                                                        <div key={detail.label} className={Style.detail}>
                                                            <dt>{detail.label}</dt>
                                                            <dd>{detail.value}</dd>
                                                        </div>
                                                    ))}
                                                </dl>
                                            ) : null}
                                            {item.date ? (
                                                <p className={Style.time}>
                                                    {item.eventLabel ? <span>{item.eventLabel}</span> : null}
                                                    <time dateTime={item.date} title={new Date(item.date).toString()}>{formatDateTime(item.date)}</time>
                                                    <span className={Style.relative}>{relativeTime(item.date)}</span>
                                                </p>
                                            ) : null}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            ) : null}
        </div>
    )
}

export default Notification
